import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { cacheLibraryCover, isCachedLibraryCover } from "@/lib/libraryCovers";
import type {
  LibraryItem,
  LibraryItemInsert,
  LibraryItemWithWorkspaces,
  LibrarySearchResult,
} from "./types";

/**
 * Fetch library items visible from one or more workspaces.
 *
 * Pass a single workspace id (legacy callers) to get items owned by that
 * workspace. Pass an array of workspace ids to get every item shared into
 * any of them — used by the cross-workspace "shared pool" UI on Plus, Pro,
 * Elite (default-shared) plans.
 *
 * Returns items deduplicated by id with their full set of workspace
 * memberships attached as `workspaceIds`.
 */
export const fetchLibraryItems = async (
  scope: string | string[],
): Promise<LibraryItemWithWorkspaces[]> => {
  const workspaceIds = Array.isArray(scope) ? scope : [scope];
  if (workspaceIds.length === 0) return [];

  const { data: linkRows, error: linkError } = await supabase
    .from("library_item_workspaces")
    .select("library_item_id, workspace_id")
    .in("workspace_id", workspaceIds);
  if (linkError) throw linkError;

  const itemIds = Array.from(new Set((linkRows ?? []).map((row) => row.library_item_id)));
  if (itemIds.length === 0) return [];

  const [{ data: items, error: itemsError }, { data: allLinks, error: allLinksError }] = await Promise.all([
    supabase
      .from("library_items")
      .select("id, workspace_id, source_provider, source_type, source_id, google_books_id, open_library_id, title, subtitle, display_title, author_text, authors, publisher, published_date, published_year, language_code, isbn_10, isbn_13, edition, volume, series, page_count, cover_url, doi, url, accessed_date, container_title, open_access_url, metadata, status, is_owned, book_binding, is_first_edition, is_signed, is_numbered, numbered_index, numbered_total, purchase_cost_cents, replacement_cost_cents, library_text_key_version, created_at, updated_at, notes:notes_preview, description:description_preview")
      .in("id", itemIds)
      .order("updated_at", { ascending: false }),
    supabase.from("library_item_workspaces").select("library_item_id, workspace_id").in("library_item_id", itemIds),
  ]);
  if (itemsError) throw itemsError;
  if (allLinksError) throw allLinksError;

  const membership = new Map<string, string[]>();
  (allLinks ?? []).forEach((row: { library_item_id: string; workspace_id: string }) => {
    const list = membership.get(row.library_item_id) ?? [];
    list.push(row.workspace_id);
    membership.set(row.library_item_id, list);
  });

  const idsByOwningWorkspace = new Map<string, string[]>();
  ((items ?? []) as LibraryItem[]).forEach((item) => {
    const list = idsByOwningWorkspace.get(item.workspace_id) ?? [];
    list.push(item.id);
    idsByOwningWorkspace.set(item.workspace_id, list);
  });

  const decryptedById = new Map<string, { notes: string | null; description: string | null }>();
  await Promise.all(
    Array.from(idsByOwningWorkspace.entries()).map(async ([owningWorkspaceId, ids]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("get_decrypted_library_items", {
        _workspace_id: owningWorkspaceId,
        _ids: ids,
      });
      if (error) return;
      ((data ?? []) as Array<{ id: string; notes: string | null; description: string | null }>).forEach((row) => {
        decryptedById.set(row.id, { notes: row.notes, description: row.description });
      });
    }),
  );

  return ((items ?? []) as LibraryItem[]).map((item) => {
    const decrypted = decryptedById.get(item.id);
    return {
      ...item,
      notes: decrypted?.notes ?? item.notes,
      description: decrypted?.description ?? item.description,
      workspaceIds: membership.get(item.id) ?? [],
    };
  });
};

/**
 * Fetch the full, decrypted `notes` and `description` for a single library
 * item. List queries hydrate these fields from `_preview` columns (capped at
 * 280 chars), so any code path that opens an item for editing MUST call this
 * helper first — otherwise the truncated preview can leak back into a save
 * and permanently overwrite the real text.
 */
export const fetchLibraryItemDecryptedText = async (
  workspaceId: string,
  itemId: string,
): Promise<{ notes: string | null; description: string | null }> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc("get_decrypted_library_items", {
    _workspace_id: workspaceId,
    _ids: [itemId],
  });
  if (error) return { notes: null, description: null };
  const row = ((data ?? []) as Array<{ id: string; notes: string | null; description: string | null }>)
    .find((r) => r.id === itemId);
  return { notes: row?.notes ?? null, description: row?.description ?? null };
};

export const saveLibraryItem = async (
  workspaceId: string,
  draft: LibrarySearchResult & { notes?: string | null },
  currentId?: string,
) => {
  const payload = {
    source_provider: draft.sourceProvider,
    source_type: draft.sourceType,
    source_id: draft.sourceId,
    google_books_id: draft.googleBooksId,
    open_library_id: draft.openLibraryId,
    title: draft.title,
    subtitle: draft.subtitle,
    display_title: draft.displayTitle,
    author_text: draft.authorText,
    authors: draft.authors,
    publisher: draft.publisher,
    published_date: draft.publishedDate,
    published_year: draft.publishedYear,
    language_code: draft.languageCode,
    isbn_10: draft.isbn10,
    isbn_13: draft.isbn13,
    edition: draft.edition,
    volume: draft.volume,
    series: draft.series,
    page_count: draft.pageCount,
    cover_url: draft.coverUrl,
    doi: draft.doi,
    url: draft.url,
    accessed_date: draft.accessedDate,
    container_title: draft.containerTitle,
    open_access_url: draft.openAccessUrl ?? null,
    metadata: draft.metadata as Json,
    status: draft.status,
    is_owned: draft.sourceType === "book" ? !!draft.isOwned : false,
    book_binding: draft.sourceType === "book" && draft.isOwned ? draft.bookBinding ?? null : null,
    is_first_edition: draft.sourceType === "book" && draft.isOwned ? !!draft.isFirstEdition : false,
    is_signed: draft.sourceType === "book" && draft.isOwned ? !!draft.isSigned : false,
    is_numbered: draft.sourceType === "book" && draft.isOwned ? !!draft.isNumbered : false,
    numbered_index: draft.sourceType === "book" && draft.isOwned && draft.isNumbered ? draft.numberedIndex ?? null : null,
    numbered_total: draft.sourceType === "book" && draft.isOwned && draft.isNumbered ? draft.numberedTotal ?? null : null,
    purchase_cost_cents: draft.sourceType === "book" && draft.isOwned ? draft.purchaseCostCents ?? null : null,
    replacement_cost_cents: draft.sourceType === "book" && draft.isOwned ? draft.replacementCostCents ?? null : null,
    citation_style_override: draft.citationStyleOverride ?? null,
  };

  const descVal = draft.description ?? null;
  const notesVal = draft.notes ?? null;

  // For edits: read the current full decrypted text BEFORE the metadata update
  // so we can detect "unchanged" and avoid overwriting the real synopsis with
  // a truncated 280-char preview that may have leaked into the draft via a
  // list-view record.
  let currentDescription: string | null = null;
  let currentNotes: string | null = null;
  if (currentId) {
    const cur = await fetchLibraryItemDecryptedText(workspaceId, currentId);
    currentDescription = cur.description;
    currentNotes = cur.notes;
  }

  let saved: LibraryItem;
  if (currentId) {
    const { data, error } = await supabase
      .from("library_items")
      .update(payload)
      .eq("id", currentId)
      .select("*")
      .single();
    if (error) throw error;
    saved = data as LibraryItem;
  } else {
    const { data, error } = await supabase
      .from("library_items")
      .insert({ workspace_id: workspaceId, ...payload } satisfies LibraryItemInsert)
      .select("*")
      .single();
    if (error) {
      if (error.message?.includes("library_items_cap_reached")) {
        throw new Error(
          "You've reached your plan's My Library cap. Remove an entry or upgrade to add more.",
        );
      }
      throw error;
    }
    saved = data as LibraryItem;
  }

  const descChanged = !currentId || (descVal ?? "") !== (currentDescription ?? "");
  const notesChanged = !currentId || (notesVal ?? "") !== (currentNotes ?? "");
  if (descChanged || notesChanged) {
    await supabase.rpc("set_library_item_text" as never, {
      _item_id: saved.id,
      _notes: notesChanged ? notesVal : null,
      _description: descChanged ? descVal : null,
    } as never);
  }

  // If the chosen cover came from an external provider (e.g. ISBNdb's
  // signed `image_original` URL), download it now while the URL is still
  // valid and cache it in the `library-covers` bucket.
  if (saved.cover_url && !isCachedLibraryCover(saved.cover_url)) {
    const { ref, error } = await cacheLibraryCover(saved.id, saved.cover_url);
    if (ref) {
      saved = { ...saved, cover_url: ref };
    } else if (error) {
      const { toast } = await import("sonner");
      toast.warning("Saved with the external cover URL — high-res caching failed.", {
        description: error,
      });
    }
  }

  return saved;
};

export const deleteLibraryItem = async (libraryItemId: string) => {
  const { data, error } = await supabase.functions.invoke("library-cover-delete", {
    body: { library_item_id: libraryItemId },
  });
  if (error) throw error;
  if (data && typeof data === "object" && "error" in data && data.error) {
    throw new Error(String((data as { error: unknown }).error));
  }
};
